import Cookies from 'js-cookie';
import {useEffect, useRef, useState} from 'react';
import useModal from '../../hooks/use-modal/use-modal';
import useTranslation from '../../hooks/use-translation/use-translation';
import {post} from '../../services/artfinder-service';
import {symbolFromCurrency} from '../../services/currencies-service';
import constantsFactory from '../../utils/constants';
import {emit, off, on} from '../../utils/events';
import ArtistAvatar from '../artist-avatar/artist-avatar';
import Button from '../button/button';
import Loading from '../loading/loading';
import {H5, P} from '../typography/typography';
import './modal-make-offer.scss';

const {EVENTS} = constantsFactory();

const ModalMakeOffer = ({artist}) => {
  const target = useRef(null);
  const modal = useModal(target);
  const [loading, setLoading] = useState(false);
  const [product, setProduct]: any = useState(null);
  const [price, setPrice]: any = useState('');
  const [initFirstName, setInitFirstName]: any = useState(null);
  const [initLastName, setInitLastName]: any = useState(null);
  const [firstName, setFirstName]: any = useState(null);
  const [lastName, setLastName]: any = useState(null);
  const [message, setMessage]: any = useState('');
  const [error, setError]: any = useState('');
  const {t} = useTranslation();

  const getSessionUserData = () => {
    let initData = {};
    const getUserSessionJwtData = (token, window) =>
      window.JSON.parse(window.atob(token.split('.')[1]));
    try {
      const token = Cookies.get('sessionid');
      initData = getUserSessionJwtData(token, window) || {};
    } catch (e) {
      console.log(e);
    }

    return initData;
  };

  useEffect(() => {
    const userData: any = getSessionUserData();
    if (userData && userData?.first_name) {
      setInitFirstName(userData.first_name);
      setFirstName(userData.first_name);
    }
    if (userData && userData?.last_name) {
      setInitLastName(userData.last_name);
      setLastName(userData.last_name);
    }
  }, []);

  useEffect(() => {
    on(EVENTS.CLICK_ON_MAKE_OFFER, modal.open);
    on(EVENTS.MAKE_OFFER_SUCCESS, modal.close);
    return () => {
      off(EVENTS.CLICK_ON_MAKE_OFFER, modal.open);
      off(EVENTS.MAKE_OFFER_SUCCESS, modal.close);
      modal.destroy();
    };
  }, [modal]);

  useEffect(() => {
    on(EVENTS.CLICK_ON_MAKE_OFFER, setProduct);
    return () => {
      off(EVENTS.CLICK_ON_MAKE_OFFER, setProduct);
    };
  });

  const resetModal = () => {
    setError('');
    setPrice('');
    setMessage('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {listed_price: product.amount};
    if (price && price.trim().length > 0) payload['price'] = price;
    if (message && message.trim().length > 0) payload['message'] = message;
    if (firstName && firstName.trim().length > 0) payload['first_name'] = firstName;
    if (lastName && price.trim().length > 0) payload['last_name'] = lastName;

    post(`/api/product/${product.slug}/make-an-offer/`, payload)
      .then(() => {
        resetModal();
        emit('MAKE_OFFER_SUCCESS');
        emit('OPEN_NOTIFICATION_SLIDER', t('Your offer has been sent successfully'));
      })
      .catch((error) => {
        let errorMsg = '';
        if (error?.price) {
          errorMsg += 'Please specify a valid offer price.' + ' ';
        }
        if (error?.first_name || error?.last_name) {
          errorMsg += 'Please supply your name when making an offer.' + ' ';
        }
        if (error?.non_field_errors) {
          errorMsg += error.non_field_errors.join(' ') + ' ';
        }
        setError(errorMsg ? errorMsg : error.detail);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div
      ref={target}
      className="modal modal-make-offer"
      tabIndex={-1}
      role="dialog"
      aria-hidden="true"
    >
      <div className="modal-dialog" role="document">
        <div className="modal-content">
          {loading && <Loading />}
          <div className="modal-header">
            <H5 style="art">{t('Make an offer')}</H5>
            {product && (
              <>
                <ArtistAvatar
                  alt={product.artist_name}
                  url={`/artist/${product.artist_slug}/availability-true/`}
                  src={artist.avatar_url}
                />

                <P style="p1" align="center">
                  {product.artist_name}&nbsp;
                  {t(`is considering offers on this
                  artwork. Please submit your offer below and they'll get back
                  to you shortly.`)}
                </P>
                <br />
                <P style="p2-b" align={'center'}>
                  {t(`Note, offers are always made in the artist's
                  chosen currency`)}
                </P>
              </>
            )}

            <button type="button" className="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          {product && (
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="modal-make-offer-listed-price">{t('Listed price')}</label>

                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">
                        {symbolFromCurrency(product.original_currency)}
                      </span>
                    </div>

                    <input
                      id="modal-make-offer-listed-price"
                      type="text"
                      className="form-control"
                      disabled
                      value={product.original_currency_amount.toFixed(2)}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="modal-make-offer-price">
                    {t('Your offer price (excluding shipping)')} *
                  </label>

                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">
                        {symbolFromCurrency(product.original_currency)}
                      </span>
                    </div>

                    <input
                      id="modal-make-offer-price"
                      type="text"
                      className={`form-control${error && ' is-invalid'}`}
                      onChange={(e) => setPrice(e.target.value)}
                      value={price}
                    />
                  </div>
                </div>

                {(!initFirstName || !initLastName) && (
                  <div>
                    <div className="form-group">
                      <label htmlFor="modal-make-offer-listed-price">
                        {t('My first name is')} *
                      </label>
                      <input
                        id="modal-make-offer-listed-price"
                        type="text"
                        name="first_name"
                        className={`form-control${error && ' is-invalid'}`}
                        placeholder="First name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="modal-make-offer-listed-price">
                        {t('My last name is')} *
                      </label>
                      <input
                        id="modal-make-offer-listed-price"
                        type="text"
                        name="last_name"
                        className={`form-control${error && ' is-invalid'}`}
                        placeholder="last name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="modal-make-offer-message">
                    {t('Message for artist (optional)')}
                  </label>

                  <textarea
                    id="modal-make-offer-message"
                    rows={5}
                    className="form-control"
                    onChange={(e) => setMessage(e.target.value)}
                    value={message}
                  />
                </div>
                <Button submit>Send offer</Button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalMakeOffer;
